import React, { Suspense, createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useMessageInstances } from '@atlassian/post-office-headless-api/hooks/useMessageInstances';

import { type ThirdPartyConfigsBootstrap } from '../../../constants/schemas/3p-config';
import {
	mapToAvailableConnectors,
	orderConfigByProductOrder,
	type useOAuthHandlers,
} from '../banner-placement';
import {
	QUICK_FIND_TEMPLATE_ID,
	type QuickFindHeadlessAvailableConnectors,
	type QuickFindHeadlessMessage,
	type QuickFindHeadlessMessageInstance,
	type QuickFindHeadlessRendererProps,
} from './headless-types';

const loadQuickFindHeadlessRenderer = () =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/search-page/quick-find-headless-message-renderer" */ './headless-renderer'
	);

const QuickFindHeadlessMessageRenderer = Object.assign(
	React.lazy<React.ComponentType<QuickFindHeadlessRendererProps>>(() =>
		loadQuickFindHeadlessRenderer().then((module) => ({
			default: module.QuickFindHeadlessMessageRenderer,
		})),
	),
	{ displayName: 'lazy(QuickFindHeadlessMessageRenderer)' },
);

interface QuickFindHeadlessPlacementProps {
	placementId: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProductsOrder: string[]; // Product keys in display order; connectors will be shown in this order
	useOAuthHandlers: useOAuthHandlers;
}

type QuickFindHeadlessPlacementData = {
	messages: QuickFindHeadlessMessageInstance[];
	availableConnectors: QuickFindHeadlessAvailableConnectors;
	useOAuthHandlers: useOAuthHandlers;
	dismissMessage: (messageInstanceId: string) => void;
	PostOfficeMessageInstance: ReturnType<
		typeof useMessageInstances<QuickFindHeadlessMessage>
	>['PostOfficeMessageInstance'];
};

const QuickFindHeadlessPlacementContext = createContext<QuickFindHeadlessPlacementData | null>(
	null,
);

const QuickFindHeadlessPlacementDataFetcher = ({
	placementId,
	thirdPartyConfigs,
	availableProductsOrder,
	useOAuthHandlers,
	onChange,
}: QuickFindHeadlessPlacementProps & {
	onChange: (value: QuickFindHeadlessPlacementData | null) => void;
}): null => {
	const { messages, dismissMessage, PostOfficeMessageInstance } =
		useMessageInstances<QuickFindHeadlessMessage>(
			{
				placementId,
				messageTemplateId: QUICK_FIND_TEMPLATE_ID,
			},
			{
				dataOutsideWrapper: true,
			},
		);

	useEffect(() => {
		void loadQuickFindHeadlessRenderer();
	}, []);

	const orderedConfigs = useMemo(
		() => orderConfigByProductOrder(thirdPartyConfigs, availableProductsOrder),
		[thirdPartyConfigs, availableProductsOrder],
	);

	const availableConnectors = useMemo(
		() => mapToAvailableConnectors(orderedConfigs),
		[orderedConfigs],
	);

	const value = useMemo(
		() => ({
			messages,
			availableConnectors,
			useOAuthHandlers,
			dismissMessage,
			PostOfficeMessageInstance,
		}),
		[messages, availableConnectors, useOAuthHandlers, dismissMessage, PostOfficeMessageInstance],
	);

	useEffect(() => {
		onChange(value);
	}, [onChange, value]);

	useEffect(() => () => onChange(null), [onChange]);

	return null;
};

/**
 * Provides Post Office headless placement data to Quick Find.
 *
 * The context provider always wraps `children`, and only the data fetcher mounts or unmounts
 * when `isEnabled` changes. This keeps the Quick Find subtree stable: swapping the wrapper in
 * on first interaction would otherwise remount the dialog mid-click and drop focus and state.
 */
export const QuickFindHeadlessPlacementDataProvider = ({
	isEnabled = true,
	children,
	...fetcherProps
}: React.PropsWithChildren<
	QuickFindHeadlessPlacementProps & { isEnabled?: boolean }
>): React.JSX.Element => {
	const [value, setValue] = useState<QuickFindHeadlessPlacementData | null>(null);

	return (
		<QuickFindHeadlessPlacementContext.Provider value={isEnabled ? value : null}>
			{isEnabled ? (
				<QuickFindHeadlessPlacementDataFetcher {...fetcherProps} onChange={setValue} />
			) : null}
			{children}
		</QuickFindHeadlessPlacementContext.Provider>
	);
};

export const QuickFindHeadlessVisiblePlacement = (): React.JSX.Element | null => {
	const placement = useContext(QuickFindHeadlessPlacementContext);

	if (!placement) {
		return null;
	}

	const {
		messages,
		availableConnectors,
		useOAuthHandlers,
		dismissMessage,
		PostOfficeMessageInstance,
	} = placement;

	return (
		<Suspense fallback={null}>
			{messages.map((message) => (
				<QuickFindHeadlessMessageRenderer
					key={message.messageInstanceId}
					message={message}
					availableConnectors={availableConnectors}
					useOAuthHandlers={useOAuthHandlers}
					dismissMessage={dismissMessage}
					PostOfficeMessageInstance={PostOfficeMessageInstance}
				/>
			))}
		</Suspense>
	);
};
